<ng-container *ngIf="activeSlice$ | async as slice">

    <h2 mat-dialog-title class="pv-dialog-title">
        <mat-icon class="material-symbols-outlined">image</mat-icon>
        <span class="pv-dialog-title-text">Edit Image Component</span>
        <button mat-icon-button class="pv-dialog-close" (click)="cancel()" matTooltip="Close" aria-label="Close dialog">
            <mat-icon>close</mat-icon>
        </button>
    </h2>

    <!-- opt-chip switcher (F-11.2) — not mat-tab-group. Hidden with a single tab (no dead UI). -->
    <div class="chip-row pv-dialog-tabs" *ngIf="tabs.length > 1">
        <ng-container *ngFor="let tab of tabs">
            <button type="button" class="opt-chip" [class.active]="(activeTab$ | async) === tab" (click)="selectTab(tab)">
                <mat-icon class="material-symbols-outlined">{{ tabIcon(tab) }}</mat-icon>
                {{ tabLabel(tab) }}
            </button>
        </ng-container>
    </div>

    <mat-dialog-content class="pv-dialog-body" *ngIf="activeTab$ | async as activeTab" (scroll)="onBodyScroll($event)">

            <!-- Static controls: they depend on no request, so they render immediately and stay put while
                 photos load. Only the results area below swaps between skeleton / error / empty / grid.
                 They also sit OUTSIDE the submitting form so Enter in the search box never applies the image.
                 Sample-tab only — the flat Logos & Graphics grid has nothing to filter or sort (F-4.1). -->
            <ng-container *ngIf="!isError(slice.status) && activeTab === ImagePickerTab.SAMPLE">
                    <div class="filter-row dialog-first-block" [formGroup]="filterGroup">
                        <div class="two-col">
                            <div class="field-block">
                                <label class="field-label">Search sample label</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-icon matPrefix>search</mat-icon>
                                    <input type="text" matInput formControlName="labelSearch" placeholder="Enter sample label">
                                </mat-form-field>
                            </div>
                            <div class="field-block">
                                <label class="field-label">Variety</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <pv-cultivar-autocomplete-chips formControlName="cultivars" [orgKey]="orgKey"></pv-cultivar-autocomplete-chips>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="two-col">
                            <div class="field-block">
                                <label class="field-label">Location(s)</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <pv-site-autocomplete-chips formControlName="sites" [orgKey]="orgKey"></pv-site-autocomplete-chips>
                                </mat-form-field>
                            </div>
                            <div class="field-block">
                                <label class="field-label">Planted/Harvested season</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-select formControlName="birthYear" placeholder="Any season">
                                        <mat-option [value]="null">Any season</mat-option>
                                        <mat-option *ngFor="let year of birthYearOptions$ | async" [value]="year">{{ year }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="two-col">
                            <div class="field-block">
                                <label class="field-label">Evaluations from</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <input matInput [matDatepicker]="evalFromPicker" formControlName="evalFrom" placeholder="Any date">
                                    <mat-datepicker-toggle matSuffix [for]="evalFromPicker"></mat-datepicker-toggle>
                                    <mat-datepicker #evalFromPicker></mat-datepicker>
                                </mat-form-field>
                            </div>
                            <div class="field-block">
                                <label class="field-label">Evaluations to</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <input matInput [matDatepicker]="evalToPicker" formControlName="evalTo" placeholder="Any date">
                                    <mat-datepicker-toggle matSuffix [for]="evalToPicker"></mat-datepicker-toggle>
                                    <mat-datepicker #evalToPicker></mat-datepicker>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="filter-actions">
                            <button type="button" class="clear-filters-link" (click)="clearFilters()">
                                <mat-icon>filter_alt_off</mat-icon>
                                Clear filters
                            </button>
                        </div>
                    </div>

                    <div class="sort-row">
                        <div class="field-block sort-axis">
                            <label class="field-label">Sort samples by</label>
                            <mat-form-field appearance="outline">
                                <mat-select [value]="sortAxis$ | async" (selectionChange)="setAxis($event.value)">
                                    <mat-option value="birthDate">Planted/Harvested season</mat-option>
                                    <mat-option value="label">Sample label</mat-option>
                                </mat-select>
                            </mat-form-field>
                        </div>

                        <!-- one toggle, not a pair: it names the direction it will switch TO -->
                        <button type="button" mat-stroked-button class="sort-toggle" (click)="toggleSort()">
                            <mat-icon>{{ (birthDateSort$ | async) === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                            {{ sortToggleLabel(sortAxis$ | async, birthDateSort$ | async) }}
                        </button>
                    </div>
            </ng-container>

            <!-- RESULTS AREA — the only part that depends on the request. dialog-first-block applies only
                 when the filter/sort block above isn't rendered (error state, or a non-Sample tab) — the
                 exact inverse of that block's own *ngIf, so whichever one is actually first gets the spacing -->
            <div class="results-area" [class.dialog-first-block]="isError(slice.status) || activeTab !== ImagePickerTab.SAMPLE">

            <!-- per-tab error: one failed slice shows here, dialog chrome + other tabs stay usable -->
            <div class="pv-dialog-state is-failure" *ngIf="isError(slice.status)">
                <mat-icon>error_outline</mat-icon>
                <p class="state-title">Couldn't load {{ activeTab === ImagePickerTab.SAMPLE ? 'field photos' : 'images' }}</p>
                <p class="state-body">Something went wrong fetching {{ activeTab === ImagePickerTab.SAMPLE ? 'photos' : 'images' }} for this tab.</p>
                <button type="button" mat-stroked-button (click)="retry()">Try again</button>
            </div>

            <!-- loading: sample-tab skeleton mirrors the real tier structure; the flat-grid tabs have no tiers to mirror (F-10/AC3) -->
            <div class="skeleton-loading" *ngIf="isLoading(slice.status) && activeTab === ImagePickerTab.SAMPLE" aria-busy="true">
                <span class="sr-only" role="status">Loading field photos…</span>
                <div class="skeleton-group" *ngFor="let group of skeletonGroups" aria-hidden="true">
                    <div class="skeleton-bar skeleton-bar--cultivar"></div>
                    <div class="skeleton-bar skeleton-bar--site"></div>
                    <div class="skeleton-bar skeleton-bar--chip"></div>
                    <div class="skeleton-grid">
                        <div class="skeleton-tile" *ngFor="let placeholder of skeletonTiles"></div>
                    </div>
                </div>
            </div>

            <div class="skeleton-grid" *ngIf="isLoading(slice.status) && activeTab !== ImagePickerTab.SAMPLE" aria-busy="true">
                <span class="sr-only" role="status">Loading images…</span>
                <div class="skeleton-tile" *ngFor="let placeholder of skeletonTiles" aria-hidden="true"></div>
            </div>

            <ng-container *ngIf="isReady(slice.status) && activeTab === ImagePickerTab.SAMPLE">
                <!-- the report's own filter matched nothing server-side -->
                <div class="pv-dialog-state" *ngIf="slice.status === Status.EMPTY">
                    <mat-icon>image_not_supported</mat-icon>
                    <p class="state-title">No photos in this report's scope</p>
                    <p class="state-body">This report's filter doesn't pull in any evaluations with photos. Adjust the report filter to widen it.</p>
                </div>

                <ng-container *ngIf="slice.status === Status.OK">
                    <!-- filters narrowed a non-empty payload to nothing — say so rather than showing a void -->
                    <div class="pv-dialog-state" *ngIf="(sampleTree$ | async)?.length === 0">
                        <mat-icon>search_off</mat-icon>
                        <p class="state-title">No photos match these filters</p>
                        <p class="state-body">Try widening the date range or clearing the variety selection.</p>
                        <button type="button" mat-stroked-button (click)="clearFilters()">Clear filters</button>
                    </div>

                    <form id="report_component_form"
                        [formGroup]="formGroup"
                        (ngSubmit)="attempt()"
                        autocomplete="off"
                    >
                    <!-- identity lives in the headers; tiles carry only the capture date -->
                    <!-- Two sticky rows, not four: cultivar·site identifies the block, sample+date identifies the run.
                         Four full-width tiers spent more vertical space on chrome than on photos. -->
                    <div class="image-tree">
                        <section class="cultivar-group" *ngFor="let cultivar of sampleTree$ | async; trackBy: trackCultivar">

                            <section class="site-group" *ngFor="let site of cultivar.sites; trackBy: trackSite">
                            <h3 class="group-header">
                                <span class="group-cultivar" [class.unknown]="cultivar.isUnknown">{{ cultivar.label }}</span>
                                <span class="group-sep">·</span>
                                <span class="group-site">{{ site.label }}</span>
                            </h3>

                            <section class="sample-group" *ngFor="let sample of site.samples; trackBy: trackSample">

                                <div class="eval-group" *ngFor="let evaluation of sample.evals; trackBy: trackEval">
                                    <!-- sample and date are one fact: a date alone says nothing about which sample it belongs to.
                                         evalStartDate is a DATE at UTC midnight, so render it in UTC to match the date filter. -->
                                    <h4 class="sample-header">
                                        <span class="sample-name">{{ sample.label }}</span>
                                        <span class="sample-sep">·</span>
                                        <span class="eval-date">
                                            {{ evaluation.hasDate ? (evaluation.evalDate | date:'d MMM y':'UTC') : 'Undated' }}
                                        </span>
                                    </h4>

                                    <div class="image-grid">
                                        <!-- select surface is its own keyboard target, distinct from the magnifier (NFR-7) -->
                                        <div class="grid-item"
                                            *ngFor="let tile of evaluation.images; trackBy: trackTile"
                                            [class.active]="tile.option.key === imageControl.value?.key"
                                            tabindex="0"
                                            role="button"
                                            [attr.aria-pressed]="tile.option.key === imageControl.value?.key"
                                            [attr.aria-label]="selectLabel(tile)"
                                            (click)="selectImage(tile.option)"
                                            (keydown)="onTileKeydown(tile, $event)"
                                        >
                                            <pv-image-ref
                                                *ngIf="tile.option.fileRef as ref"
                                                [fileRef]="ref"
                                                transformId="thumb_300"
                                                [square]="true"
                                                [lazy]="true"
                                            >
                                            </pv-image-ref>

                                            <!-- a real button in tab order, never hover-gated; stopPropagation keeps it off the select surface (NFR-7) -->
                                            <button type="button"
                                                mat-icon-button
                                                class="tile-magnifier"
                                                (click)="openPreview(tile, $event)"
                                                [attr.aria-label]="previewLabel(tile)"
                                            >
                                                <mat-icon>zoom_in</mat-icon>
                                            </button>

                                            <span class="capture-scrim"
                                                [matTooltip]="tile.isUploadDate ? 'Upload date; capture date unknown.' : null"
                                            >
                                                {{ tile.isUploadDate ? '~' : '' }}{{ tile.captureDate | date:'d MMM yy' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            </section>
                        </section>
                    </div>
                    </form>

                    <!-- infinite scroll (Sample Photos only) — spinner rides below the tree, keyed off loadingMore
                         so it never touches `status` and the already-rendered tiles never disappear behind it -->
                    <div class="load-more-row" *ngIf="slice.loadingMore" aria-live="polite">
                        <mat-spinner diameter="24"></mat-spinner>
                        <span>Loading more photos…</span>
                    </div>
                </ng-container>
            </ng-container>

            <!-- dropzone (Logos & Graphics) — never waits on the existing-images request below. Each valid file
                 uploads immediately as a batch (AC: multiple files at once); nothing is staged the way the Upload
                 tab stages files, since an org asset is a persistent library item, not the current selection.
                 Moved above the existing-assets grid so it's the first thing a user sees on this tab. -->
            <ng-container *ngIf="activeTab === ImagePickerTab.ORG">
                <div class="asset-dropzone field-block"
                    *ngIf="canWriteAssets"
                    [class.dragging]="isDraggingOrgFile"
                    (drop)="onOrgDrop($event)"
                    (dragover)="onOrgDragOver($event)"
                    (dragleave)="onOrgDragLeave($event)"
                >
                    <p class="dropzone-copy">
                        Drop images, or browse, to add them to your organisation's shared library. These images are
                        organisation-specific and persist between reports.
                    </p>
                    <label class="dropzone-browse">
                        Browse Files
                        <input type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp" [disabled]="orgUploadPending" (change)="onOrgFileSelected($event)">
                    </label>
                    <p class="dropzone-error" *ngFor="let error of orgUploadErrors">{{ error }}</p>
                </div>

                <!-- read-only members can browse and select, but the upload control is absent — never present-but-disabled -->
                <p class="asset-readonly-notice" *ngIf="!canWriteAssets">
                    Your role can select existing images but not add or remove them.
                </p>
            </ng-container>

            <!-- Logos & Graphics tab (story 2.3) — flat grid, no grouping (AD-8/F-4.1). -->
            <ng-container *ngIf="isReady(slice.status) && activeTab === ImagePickerTab.ORG">
                <div class="pv-dialog-state" *ngIf="slice.status === Status.EMPTY">
                    <mat-icon>image_not_supported</mat-icon>
                    <p class="state-title">No shared images yet</p>
                    <p class="state-body">
                        Anything your team uploads here is available to every report.
                        <ng-container *ngIf="hasLegacyLetterheads">
                            Letterheads from the old report system aren't migrated — re-upload anything you still need.
                        </ng-container>
                    </p>

                    <!-- the very first upload into an empty library — the OK-only grid below never renders here -->
                    <div class="image-grid" *ngIf="orgUploadPending">
                        <div class="skeleton-tile" *ngFor="let placeholder of uploadingPlaceholders(orgUploadingCount)" aria-hidden="true"></div>
                    </div>
                </div>

                <form id="report_component_form"
                    *ngIf="slice.status === Status.OK"
                    [formGroup]="formGroup"
                    (ngSubmit)="attempt()"
                    autocomplete="off"
                >
                    <div class="image-grid">
                        <div class="asset-tile" *ngFor="let option of slice.options; trackBy: trackAssetOption">
                            <div class="grid-item"
                                [class.active]="option.key === imageControl.value?.key"
                                [class.tile-deleting]="isDeleting(option)"
                                tabindex="0"
                                role="button"
                                [attr.aria-pressed]="option.key === imageControl.value?.key"
                                [attr.aria-label]="assetSelectLabel(option)"
                                (click)="selectImage(option)"
                                (keydown)="onAssetKeydown(option, $event)"
                            >
                                <pv-image-ref
                                    *ngIf="option.fileRef as ref"
                                    [fileRef]="ref"
                                    transformId="thumb_300"
                                    [square]="true"
                                    [lazy]="true"
                                >
                                </pv-image-ref>

                                <!-- covers the tile in place rather than removing it early — the tile disappears
                                     only once the delete actually succeeds and the option drops out of slice.options -->
                                <div class="tile-deleting-overlay" *ngIf="isDeleting(option)" aria-hidden="true">
                                    <mat-spinner diameter="20"></mat-spinner>
                                </div>

                                <button type="button"
                                    mat-icon-button
                                    class="tile-magnifier"
                                    (click)="openAssetPreview(option, $event)"
                                    [attr.aria-label]="assetPreviewLabel(option)"
                                >
                                    <mat-icon>zoom_in</mat-icon>
                                </button>

                                <!-- read-only members can browse and select, but never delete (F-4's role gate) -->
                                <button type="button"
                                    mat-icon-button
                                    class="tile-delete"
                                    *ngIf="canWriteAssets"
                                    [disabled]="isDeleting(option)"
                                    (click)="deleteAsset(option, $event)"
                                    [attr.aria-label]="'Delete image — ' + (option.name || 'Untitled image')"
                                >
                                    <mat-icon>delete</mat-icon>
                                </button>
                            </div>

                            <!-- name renders beneath the tile, not overlaid on it (F-4.7); rename swaps it for an inline editor -->
                            <ng-container *ngIf="isRenaming(option); else orgNameDisplay">
                                <div class="asset-rename" (click)="$event.stopPropagation()">
                                    <input type="text" class="asset-rename-input" [formControl]="renameControl" maxlength="255"
                                        [readonly]="renamePending" (keydown)="onRenameKeydown(option, $event)">
                                    <button type="button" class="asset-rename-btn" [disabled]="renamePending" (click)="commitRename(option, $event)" aria-label="Save name">
                                        <mat-icon>check</mat-icon>
                                    </button>
                                    <button type="button" class="asset-rename-btn" [disabled]="renamePending" (click)="cancelRename($event)" aria-label="Cancel rename">
                                        <mat-icon>close</mat-icon>
                                    </button>
                                </div>
                            </ng-container>
                            <ng-template #orgNameDisplay>
                                <span class="asset-name">
                                    <span class="asset-name-text" [title]="option.name">{{ option.name }}</span>
                                    <button type="button" mat-icon-button class="rename-trigger" *ngIf="canWriteAssets"
                                        (click)="startRename(option, $event)"
                                        [attr.aria-label]="'Rename ' + (option.name || 'Untitled image')"
                                    >
                                        <mat-icon>edit</mat-icon>
                                    </button>
                                </span>
                            </ng-template>
                        </div>

                        <!-- uploading — shown even while slice.options is still the pre-upload list, so a drop/browse
                             gets an immediate skeleton instead of the grid looking unchanged until the POST resolves -->
                        <div class="skeleton-tile" *ngFor="let placeholder of uploadingPlaceholders(orgUploadingCount)" aria-hidden="true"></div>
                    </div>
                </form>

                <div class="load-more-row" *ngIf="slice.loadingMore" aria-live="polite">
                    <mat-spinner diameter="24"></mat-spinner>
                    <span>Loading more images…</span>
                </div>
            </ng-container>

            <!-- dropzone (Upload tab) — never waits on the existing-uploads request below. Each valid file
                 uploads immediately as a batch, straight into this report's own asset list. Moved above the
                 existing-assets grid so it's the first thing a user sees on this tab. -->
            <ng-container *ngIf="activeTab === ImagePickerTab.REPORT">
                <div class="asset-dropzone field-block"
                    *ngIf="canWriteAssets"
                    [class.dragging]="isDraggingReportFile"
                    (drop)="onReportDrop($event)"
                    (dragover)="onReportDragOver($event)"
                    (dragleave)="onReportDragLeave($event)"
                >
                    <p class="dropzone-copy">
                        Drop images, or browse. Use this for anything not already in Culteva — logos, external charts, screenshots.
                        These images are specific to this report.
                    </p>
                    <label class="dropzone-browse">
                        Browse Files
                        <input type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp" [disabled]="reportUploadPending" (change)="onReportFileSelected($event)">
                    </label>
                    <p class="dropzone-error" *ngFor="let error of reportUploadErrors">{{ error }}</p>
                </div>

                <!-- read-only members never see a present-but-disabled dropzone (AC 7) -->
                <p class="asset-readonly-notice" *ngIf="!canWriteAssets">
                    Your role can use existing images but not upload new ones.
                </p>
            </ng-container>

            <!-- Upload tab — report-scoped, uploads immediately (same pattern as Logos & Graphics), multi-file. -->
            <ng-container *ngIf="isReady(slice.status) && activeTab === ImagePickerTab.REPORT">
                <div class="pv-dialog-state" *ngIf="slice.status === Status.EMPTY">
                    <mat-icon>image_not_supported</mat-icon>
                    <p class="state-title">No uploads in this report yet</p>

                    <div class="image-grid" *ngIf="reportUploadPending">
                        <div class="skeleton-tile" *ngFor="let placeholder of uploadingPlaceholders(reportUploadingCount)" aria-hidden="true"></div>
                    </div>
                </div>

                <form id="report_component_form"
                    *ngIf="slice.status === Status.OK"
                    [formGroup]="formGroup"
                    (ngSubmit)="attempt()"
                    autocomplete="off"
                >
                    <div class="image-grid">
                        <div class="asset-tile" *ngFor="let option of slice.options; trackBy: trackAssetOption">
                            <div class="grid-item"
                                [class.active]="option.key === imageControl.value?.key"
                                [class.tile-deleting]="isDeleting(option)"
                                tabindex="0"
                                role="button"
                                [attr.aria-pressed]="option.key === imageControl.value?.key"
                                [attr.aria-label]="assetSelectLabel(option)"
                                (click)="selectImage(option)"
                                (keydown)="onAssetKeydown(option, $event)"
                            >
                                <pv-image-ref
                                    *ngIf="option.fileRef as ref"
                                    [fileRef]="ref"
                                    transformId="thumb_300"
                                    [square]="true"
                                    [lazy]="true"
                                >
                                </pv-image-ref>

                                <div class="tile-deleting-overlay" *ngIf="isDeleting(option)" aria-hidden="true">
                                    <mat-spinner diameter="20"></mat-spinner>
                                </div>

                                <button type="button"
                                    mat-icon-button
                                    class="tile-magnifier"
                                    (click)="openAssetPreview(option, $event)"
                                    [attr.aria-label]="assetPreviewLabel(option)"
                                >
                                    <mat-icon>zoom_in</mat-icon>
                                </button>

                                <!-- read-only members can browse and select, but never delete (F-4's role gate) -->
                                <button type="button"
                                    mat-icon-button
                                    class="tile-delete"
                                    *ngIf="canWriteAssets"
                                    [disabled]="isDeleting(option)"
                                    (click)="deleteAsset(option, $event)"
                                    [attr.aria-label]="'Delete image — ' + (option.name || 'Untitled image')"
                                >
                                    <mat-icon>delete</mat-icon>
                                </button>
                            </div>

                            <ng-container *ngIf="isRenaming(option); else reportNameDisplay">
                                <div class="asset-rename" (click)="$event.stopPropagation()">
                                    <input type="text" class="asset-rename-input" [formControl]="renameControl" maxlength="255"
                                        [readonly]="renamePending" (keydown)="onRenameKeydown(option, $event)">
                                    <button type="button" class="asset-rename-btn" [disabled]="renamePending" (click)="commitRename(option, $event)" aria-label="Save name">
                                        <mat-icon>check</mat-icon>
                                    </button>
                                    <button type="button" class="asset-rename-btn" [disabled]="renamePending" (click)="cancelRename($event)" aria-label="Cancel rename">
                                        <mat-icon>close</mat-icon>
                                    </button>
                                </div>
                            </ng-container>
                            <ng-template #reportNameDisplay>
                                <span class="asset-name">
                                    <span class="asset-name-text" [title]="option.name">{{ option.name }}</span>
                                    <button type="button" mat-icon-button class="rename-trigger" *ngIf="canWriteAssets"
                                        (click)="startRename(option, $event)"
                                        [attr.aria-label]="'Rename ' + (option.name || 'Untitled image')"
                                    >
                                        <mat-icon>edit</mat-icon>
                                    </button>
                                </span>
                            </ng-template>
                        </div>

                        <div class="skeleton-tile" *ngFor="let placeholder of uploadingPlaceholders(reportUploadingCount)" aria-hidden="true"></div>
                    </div>
                </form>

                <div class="load-more-row" *ngIf="slice.loadingMore" aria-live="polite">
                    <mat-spinner diameter="24"></mat-spinner>
                    <span>Loading more images…</span>
                </div>
            </ng-container>
            </div>
    </mat-dialog-content>

    <!-- always rendered, so Cancel is reachable while photos load. Apply is synchronous on every tab now —
         both Upload and Logos & Graphics commit their uploads immediately on drop/browse, not on Apply. -->
    <mat-dialog-actions align="end" class="pv-dialog-footer">
        <button type="button" mat-stroked-button (click)="cancel()">Cancel</button>
        <button type="button" mat-flat-button color="primary" (click)="attempt()">
            <mat-icon>check</mat-icon>
            Apply
        </button>
    </mat-dialog-actions>

</ng-container>
